/**
 * AmbientIntelligenceBar — Felix's proactive intelligence layer.
 *
 * A slim, animated bar that appears below the topbar on every page,
 * showing Felix's contextual insights based on:
 * - Current page context (project, entity type, filters)
 * - User's open tasks, overdue items, upcoming deadlines
 * - System health (conflicts, credit usage, sprint status)
 * - AI-generated nudges and suggestions
 *
 * This is the "unimagined" feature — Felix doesn't wait for you to ask.
 * He observes, analyzes, and surfaces what matters NOW.
 *
 * Behavior:
 * - Fetches briefing data on mount + periodic refresh (2 min)
 * - Shows highest-priority insight with contextual action
 * - Cycles through insights with auto-rotate (8s per insight)
 * - Collapses to single line; expands on hover/click
 * - Dismissible per-session; re-appears on new insights
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, m } from "framer-motion";
// Only the icons this COMPONENT renders. The insight icons moved with
// buildInsights into ambientInsights.tsx; several of the rest (CheckCircle2,
// Clock, TrendingUp, CreditCard) were already unused before that move.
import { ChevronRight, X, Bot } from "lucide-react";
import { useSidebarStore } from "@/stores/sidebar";
import { useFelixPanelStore } from "@/stores/felixPanel";
import { cn } from "@/lib/utils";
import { assistantApi } from "@/api/services";
import { buildInsights, type Insight } from "@/components/ambientInsights";
import { usePresenting } from "@/stores/presenting";

const ROTATE_INTERVAL = 8000; // ms per insight

/** The bar is `fixed`, so it takes no space in the flow; AppShell's <main>
 *  pads by this variable so the bar never covers a page heading (row 8660d501:
 *  "Audit Log", "Roles" and "Change Control" rendered half-hidden under it). */
export const AMBIENT_BAR_HEIGHT_VAR = "--ambient-bar-height";

/** Publishes the element's rendered height on <html>, and 0px once it is gone. */
export function useReservedHeight(el: HTMLElement | null) {
  useEffect(() => {
    const root = document.documentElement;
    if (!el) {
      root.style.setProperty(AMBIENT_BAR_HEIGHT_VAR, "0px");
      return;
    }
    const publish = () => root.style.setProperty(AMBIENT_BAR_HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.setProperty(AMBIENT_BAR_HEIGHT_VAR, "0px");
    };
  }, [el]);
}

// ── Insight types ──────────────────────────────────────────────────

const DISMISS_KEY = "ftl.ambientBar.dismissed";

export function AmbientIntelligenceBar() {
  const location = useLocation();
  const navigate = useNavigate();
  // Dismissed for the browser session, not just until the next remount or the
  // next change in the count (founder 6415500288 item 4: "or can be dismissed
  // for the session"). Storage can be unavailable; then it is per mount, as before.
  const [dismissed, setDismissedState] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const setDismissed = useCallback((v: boolean) => {
    setDismissedState(v);
    try {
      if (v) window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* storage unavailable: dismissed for this mount only */
    }
  }, []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [barEl, setBarEl] = useState<HTMLDivElement | null>(null);
  useReservedHeight(barEl);

  // KI-0195 follow-up: shift bar right when Felix panel is open so content
  // centering doesn't start inside Felix's panel area.
  const felixMode = useSidebarStore((s) => s.felixMode);
  const felixOpen = useFelixPanelStore((s) => s.open);
  const felixWidth = useFelixPanelStore((s) => s.width);
  const leftOffset =
    felixMode && felixOpen
      ? `calc(var(--sidebar-width) + ${felixWidth}px)`
      : "var(--sidebar-width)";

  // Fetch briefing data
  const { data: briefing } = useQuery({
    queryKey: ["felix-briefing"],
    queryFn: () => assistantApi.getBriefing(),
    staleTime: 2 * 60 * 1000, // 2 min
    refetchInterval: 2 * 60 * 1000,
    retry: 1,
  });

  const presenting = usePresenting((st) => st.presenting);
  const insights = useMemo(
    () => buildInsights(briefing, location.pathname, presenting),
    [briefing, location.pathname, presenting],
  );

  // Auto-rotate insights
  useEffect(() => {
    if (insights.length <= 1 || paused) return;
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % insights.length);
    }, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [insights.length, paused]);

  // Reset index when insights change (a dismissal stands for the session).
  useEffect(() => {
    setActiveIndex(0);
  }, [insights.length]);

  const handleAction = useCallback(
    (action?: Insight["action"]) => {
      if (!action) return;
      if (action.onClick) action.onClick();
      if (action.href) navigate(action.href);
    },
    [navigate],
  );

  if (dismissed || insights.length === 0) return null;

  const current = insights[activeIndex % insights.length];
  if (!current) return null;
  const Icon = current.icon;

  return (
    <m.div
      ref={setBarEl}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="fixed top-[var(--topbar-height)] right-0 z-[46] topbar-left-offset transition-[left] duration-300"
      style={{ "--desktop-left": leftOffset } as React.CSSProperties}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="bg-white dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200/70 dark:border-slate-800 px-4 py-2">
        <div className="flex items-center gap-3 max-w-7xl mx-auto">
          {/* Felix mini avatar */}
          <div className="shrink-0">
            <div className="h-6 w-6 rounded-full bg-brand-500/15 flex items-center justify-center">
              <Bot className="h-3.5 w-3.5 text-brand-500" />
            </div>
          </div>

          {/* Insight content with crossfade */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <AnimatePresence mode="wait">
              <m.div
                key={current.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-2.5"
              >
                <div className={cn("shrink-0 p-1 rounded-md", current.bgColor)}>
                  <Icon className={cn("h-3.5 w-3.5", current.iconColor)} />
                </div>
                <span className="text-xs text-slate-900 dark:text-slate-100 truncate font-medium">
                  {current.message}
                </span>
                {current.action && (
                  <button
                    onClick={() => handleAction(current.action)}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
                  >
                    {current.action.label}
                    <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </m.div>
            </AnimatePresence>
          </div>

          {/* Insight counter dots */}
          {insights.length > 1 && (
            <div className="flex items-center gap-1 shrink-0">
              {insights.slice(0, 5).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    i === activeIndex % insights.length
                      ? "w-4 bg-brand-500"
                      : "w-1.5 bg-text-tertiary/30 hover:bg-text-tertiary/50",
                  )}
                  aria-label={`Show insight ${i + 1}`}
                />
              ))}
              {insights.length > 5 && (
                <span className="text-[9px] text-slate-500 dark:text-slate-400 ml-0.5">+{insights.length - 5}</span>
              )}
            </div>
          )}

          {/* Dismiss */}
          <button
            onClick={() => setDismissed(true)}
            className="shrink-0 p-1 rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Dismiss insights"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </m.div>
  );
}
